/**
 * FIELD-1 -> FIELD-2 wall migration (PRP-SKETCH-FIELD-2 E6, DD-SF2.2).
 *
 * A v2 room with no `walls` map is a FIELD-1 room. FIELD-1 drew every shared wall
 * at `legacy_interior_wall_in` (4.5 in) and split rooms at 0 in. On open, each wall
 * of a FIELD-1 room that faces a neighbour at one of those widths (within the
 * shared-wall tolerance, and nearer it than the 4 in default) is stamped with it on
 * BOTH rooms, so the wall stays shared at its own thickness and no corner moves.
 * Every FIELD-1 room then gets a `walls` map (possibly empty), so this runs once.
 */
import { POLICY, SHARED_TOL_FT, inToFt } from './policy';
import { facingWalls } from './overlap';
import type { Plan, Room } from './types';

export interface MigrationResult {
  /** FIELD-1 rooms that got a walls map. */
  rooms: string[];
  /** Walls stamped, as room id + start corner id + inches. */
  stamped: { rid: string; vid: number; thicknessIn: number }[];
}

/** A FIELD-1 room: no walls map yet. */
export const isLegacyRoom = (r: Room): boolean => r.walls == null;

/** The legacy width a gap was drawn at, inches, or null when it is the current default (or no wall). */
function legacyWidthIn(g: number): number | null {
  const def = inToFt(POLICY.interior_wall_in);
  for (const cand of [POLICY.legacy_interior_wall_in, 0]) {
    const c = inToFt(cand);
    if (Math.abs(g - c) <= SHARED_TOL_FT && Math.abs(g - c) < Math.abs(g - def)) return cand;
  }
  return null;
}

export function migrateLegacyWalls(plan: Plan): MigrationResult {
  const legacy = plan.rooms.filter(isLegacyRoom);
  const res: MigrationResult = { rooms: legacy.map((r) => r.id), stamped: [] };
  if (!legacy.length) return res;
  const isOld = new Set(res.rooms);
  const stamp = (r: Room, vid: number, inches: number) => {
    if (!r.walls) r.walls = {};
    if (r.walls[vid]) return; // an explicit entry wins
    r.walls[vid] = { thicknessIn: inches };
    res.stamped.push({ rid: r.id, vid, thicknessIn: inches });
  };
  // Maps are created after the pass: a room's own map must not hide it from the test.
  const rooms = plan.rooms;
  for (let i = 0; i < rooms.length; i++) {
    for (let j = i + 1; j < rooms.length; j++) {
      const A = rooms[i], B = rooms[j];
      if (!isOld.has(A.id) && !isOld.has(B.id)) continue;
      for (const f of facingWalls(A, B, POLICY.shared_min_overlap_ft)) {
        const w = legacyWidthIn(f.g);
        if (w == null) continue;
        stamp(A, f.a1.id, w);
        stamp(B, f.b1.id, w);
      }
    }
  }
  for (const r of legacy) if (!r.walls) r.walls = {};
  return res;
}
